import { environment } from '@environments/environment';
import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { AuthenticationGuard } from '@core/guards/auth/authentication.guard';
import { RedirectGuard } from '@core/guards/redirect.guard';
import { SubscriptionService } from '@core/services/subscriptions.service';
import { AppRoute } from '@core/types/app-route.interface';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import {
  demandForecastingEffects,
  demandForecastingReducer,
} from 'demand-forecasting/store/demand-forecasting.feature';
import { DEMAND_FORECASTING_STATE_FEATURE_KEY } from 'demand-forecasting/store/demand-forecasting.key';
import { filter, map, take } from 'rxjs';
import { FeatureGuard } from './guards/feature.guard';
import { RequiredStateResolver } from './guards/required-state.guard';
import { BasicLayoutComponent } from './layouts/basic-layout/basic-layout.component';
import { AccessDeniedPageComponent } from './pages/access-denied/access-denied-page.component';
import { NotFoundPageComponent } from './pages/not-found/not-found-page.component';
import { ProfilePageComponent } from './pages/profile/profile-page.component';
import { FeatureFlagSelectors } from './store/feature-flags/feature-flags.selectors';

/**
 * Route guard factory that waits for subscription data to load before checking
 * module access. Returns an Observable so Angular defers navigation until the
 * async httpResource in SubscriptionService has resolved.
 *
 * Usage: canActivate: [requireSubscription((s) => s.isReplenishmentEnabled())]
 */
export const requireSubscription =
  (check: (s: SubscriptionService) => boolean) => () => {
    const service = inject(SubscriptionService);
    const router = inject(Router);

    return toObservable(service.isLoaded).pipe(
      filter(Boolean),
      take(1),
      map(() => check(service) || router.parseUrl('/access-denied')),
    );
  };

export const APP_ROUTES: AppRoute[] = [
  {
    path: '',
    canActivate: [AuthenticationGuard, RequiredStateResolver],
    component: BasicLayoutComponent,
    children: [
      {
        path: '',
        canActivate: [RedirectGuard],
        // note: it's still necessary to have a component here, otherwise the redirect guard will not work
        // until our "home screen" refinements are done, this should be fine.
        component: BasicLayoutComponent,
      },
      {
        path: 'home',
        loadChildren: () =>
          import('home/home.routes').then((m) => m.HOME_ROUTES),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('settings/settings.routes').then((m) => m.SETTINGS_ROUTES),
        canActivate: [
          requireSubscription(
            (s) => s.isReplenishmentEnabled() || s.isDfcEnabled(),
          ),
        ],
      },

      {
        path: 'commodity-price',
        loadChildren: () =>
          import('commodity-price/commodity-price.routes').then(
            (m) => m.COMMODITY_PRICE_ROUTES,
          ),
        canActivate: [requireSubscription((s) => s.isCpfEnabled())],
      },

      {
        path: 'demand',
        loadChildren: () =>
          import('demand-forecasting/demand-forecasting.routes').then(
            (m) => m.DEMAND_FORECASTING_ROUTES,
          ),
        providers: [
          provideState(
            DEMAND_FORECASTING_STATE_FEATURE_KEY,
            demandForecastingReducer,
          ),
          provideEffects(demandForecastingEffects),
        ],

        canActivate: [requireSubscription((s) => s.isDfcEnabled())],
      },
      {
        path: 'insight',
        canActivate: [
          FeatureGuard.forFeature(
            FeatureFlagSelectors.selectFeatureFlagById(
              FeatureFlagId.tmp_new_insights_tab,
            ),
          ),
        ],
        loadChildren: () =>
          import('insight/insight.routes').then((m) => m.INSIGHT_ROUTES),
      },

      {
        path: 'consensus-cycles',
        loadChildren: () =>
          import('consensus/cycles/consensus-cycles.routes').then(
            (m) => m.CONSENSUS_CYCLES_ROUTES,
          ),
        // the cycle detail page reuses the consensus grid, which depends on the
        // demand-forecasting state/effects
        providers: [
          provideState(
            DEMAND_FORECASTING_STATE_FEATURE_KEY,
            demandForecastingReducer,
          ),
          provideEffects(demandForecastingEffects),
        ],
      },

      {
        path: 'user-management',
        loadChildren: () =>
          import('user-management/user-management.routes').then(
            (m) => m.USER_MANAGEMENT_ROUTES,
          ),
      },
      {
        path: 'access-groups',
        loadChildren: () =>
          import('access-group/access-group.routes').then(
            (m) => m.ACCESS_GROUP_ROUTES,
          ),
      },
      {
        path: 'replenishment',
        loadChildren: () =>
          import('replenishment/replenishment.routes').then(
            (m) => m.REPLENISHMENT_ROUTES,
          ),
        canActivate: [requireSubscription((s) => s.isReplenishmentEnabled())],
      },
      {
        path: 'planning',
        children: [
          {
            path: 'event-extraction',
            loadChildren: () =>
              import('event-extraction/event-extraction.routes').then(
                (m) => m.EVENT_EXTRACTION_ROUTES,
              ),
            canActivate: [
              FeatureGuard.forFeature(
                FeatureFlagSelectors.selectEventExtractionFeatureEnabled,
              ),
            ],
          },
        ],
      },
      {
        path: '',
        redirectTo: 'demand/configurations', // TODO remove this when the dashboard is ready!!!
        pathMatch: 'full',
      },
      {
        path: 'configurations',
        redirectTo: 'demand/configurations', // TODO remove this when the dashboard is ready!!!
      },
      {
        path: 'forecast/configurations',
        redirectTo: 'demand/configurations', // TODO remove this when the dashboard is ready!!!
      },
      {
        path: 'documentation',
        canMatch: [() => environment.environment === 'development'],
        loadComponent: () =>
          import('./pages/documentation/documentation-page.component').then(
            (m) => m.DocumentationPageComponent,
          ),
      },
      {
        path: 'profile',
        component: ProfilePageComponent,
      },
      {
        path: 'access-denied',
        component: AccessDeniedPageComponent,
      },
      {
        path: '**',
        component: NotFoundPageComponent,
      },
    ],
  },
];
